import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/common/Widgets";
import { Play, Trash2, Inbox, FileText, User, ArrowRight, RefreshCw } from "lucide-react";
import { fmtDate } from "@/lib/format";

export default function DraftsPage() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [deleting, setDeleting] = useState(null);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/drafts?limit=100");
      setDrafts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Failed to load drafts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const resume = (d) => {
    if (d.kind === "customer") {
      navigate(`/customers/new?draft=${d.id}`);
    } else if (d.kind === "application") {
      navigate(`/applications/new?draft=${d.id}`);
    }
  };

  const remove = async (d) => {
    setDeleting(d.id);
    try {
      await api.delete(`/drafts/${d.id}`);
      setDrafts((prev) => prev.filter((x) => x.id !== d.id));
      toast.success("Draft deleted");
    } catch (e) {
      toast.error("Could not delete draft");
    } finally {
      setDeleting(null);
    }
  };

  const filtered = tab === "all" ? drafts : drafts.filter((d) => d.kind === tab);
  const counts = {
    all: drafts.length,
    customer: drafts.filter((d) => d.kind === "customer").length,
    application: drafts.filter((d) => d.kind === "application").length,
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="drafts-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <SectionHeader
          title="My Drafts"
          subtitle="Unfinished customer onboarding and loan application forms. Auto-saved every few seconds while you work."
        />
        <Button variant="secondary" size="sm" onClick={fetchDrafts} disabled={loading} data-testid="drafts-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="drafts-tabs">
          <TabsTrigger value="all" data-testid="drafts-tab-all">
            All <Badge variant="outline" className="ml-2 text-[10px]">{counts.all}</Badge>
          </TabsTrigger>
          <TabsTrigger value="customer" data-testid="drafts-tab-customer">
            <User className="h-3.5 w-3.5 mr-1" />Customers <Badge variant="outline" className="ml-2 text-[10px]">{counts.customer}</Badge>
          </TabsTrigger>
          <TabsTrigger value="application" data-testid="drafts-tab-application">
            <FileText className="h-3.5 w-3.5 mr-1" />Applications <Badge variant="outline" className="ml-2 text-[10px]">{counts.application}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <Card className="p-4 space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </Card>
          ) : filtered.length === 0 ? (
            <Card className="p-12 text-center border-dashed">
              <Inbox className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
              <h3 className="text-base font-semibold heading">No drafts yet</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                When you start a customer onboarding or loan application and don't finish, your progress will be auto-saved here. You can return any time to resume.
              </p>
              <div className="flex justify-center gap-2 mt-4">
                <Button variant="secondary" onClick={() => navigate("/customers/new")} data-testid="drafts-empty-new-customer">
                  <User className="h-4 w-4 mr-1" />Onboard Customer
                </Button>
                <Button onClick={() => navigate("/applications/new")} data-testid="drafts-empty-new-application">
                  <FileText className="h-4 w-4 mr-1" />New Application
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Display name</TableHead>
                    <TableHead>Step</TableHead>
                    <TableHead>Last updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((d) => (
                    <TableRow key={d.id} data-testid={`draft-row-${d.id}`}>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={d.kind === "customer" ? "border-accent/40 text-accent" : "border-primary/40 text-primary"}
                        >
                          {d.kind === "customer" ? <User className="h-3 w-3 mr-1" /> : <FileText className="h-3 w-3 mr-1" />}
                          {d.kind}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{d.meta?.display_name || "—"}</div>
                        {d.meta?.loan_type && (
                          <div className="text-xs text-muted-foreground mono">{d.meta.loan_type}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="mono text-xs">{(d.step ?? 0) + 1}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">{fmtDate(d.updated_at)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            onClick={() => resume(d)}
                            data-testid={`draft-resume-${d.id}`}
                          >
                            <Play className="h-3.5 w-3.5 mr-1" />Resume
                            <ArrowRight className="h-3.5 w-3.5 ml-0.5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={deleting === d.id}
                                data-testid={`draft-delete-${d.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently remove your saved progress for <b>{d.meta?.display_name || d.kind}</b>. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`draft-delete-cancel-${d.id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => remove(d)}
                                  data-testid={`draft-delete-confirm-${d.id}`}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
